import { MetaReducer, createReducer, on } from '@ngrx/store';

import {
  clearPortfolioUPPDState,
  initPropertySelector,
  removeSelectedCollectionId,
  restorePropertySelectorState,
  setCollectionUPPDState,
  setDoNotStore,
  setJoinedProperties,
  setPortfolioUPPDState,
  setPropertySelectorSwitchStatus,
  setPropertySingleMode,
  setSelectedCollectionIds,
  setSelectedPortfolioId,
  setSelectedPropertyIds,
} from '@dashboards/store/actions/property-selector.actions';
import { allPortfoliosValue } from '@main-application/application-menu/property-selector/constants';
import {
  dynamicHydrationMetaReducer,
  patchInitialValue,
} from '@main-application/store/reducers/hydration-meta.reducer';
import { ICollectionUPPDState, IPortfolioUPPDState } from '@shared/enums/property-type.enum';

export const propertySelectorFeatureKey = 'property-selector';
let dynamicPropertySelectorFeatureKey = '';

const getPropertySelectorFeatureKey = (tenantId: number, userId: number) =>
  `${propertySelectorFeatureKey}_${tenantId}_${userId}`;

export enum PropertySelectorSwitchStatus {
  PORTFOLIO = 'portfolio',
  COLLECTION = 'collection',
}

export interface PropertySelectorState {
  portfolioId?: number;
  propertySelectorSwitchStatus: PropertySelectorSwitchStatus.PORTFOLIO | PropertySelectorSwitchStatus.COLLECTION;
  singleModeEnabled: boolean;
  propertyIds?: number[];
  collectionIds?: number[];
  allPropertiesSelected?: boolean;
  allCollectionsSelected?: boolean;
  joinProperties: boolean;
  doNotStore: boolean;
  portfolioUppdState: IPortfolioUPPDState;
  collectionUppdState: ICollectionUPPDState;
}

export const initialState: PropertySelectorState = patchInitialValue(
  {
    portfolioId: allPortfoliosValue,
    propertySelectorSwitchStatus: PropertySelectorSwitchStatus.PORTFOLIO,
    propertyIds: undefined,
    singleModeEnabled: false,
    allPropertiesSelected: true,
    allCollectionsSelected: true,
    joinProperties: false,
    doNotStore: false,
    portfolioUppdState: null,
    collectionUppdState: null,
  },
  propertySelectorFeatureKey
);

export const metaPropertySelectorReducers: Array<MetaReducer<any, any>> = [
  dynamicHydrationMetaReducer<PropertySelectorState>(
    () => dynamicPropertySelectorFeatureKey,
    propertySelectorFeatureKey
  ),
];

export const propertySelectorReducer = createReducer(
  initialState,
  on(initPropertySelector, (state, action) => {
    dynamicPropertySelectorFeatureKey = getPropertySelectorFeatureKey(action.tenantId, action.userId);
    return {
      ...patchInitialValue(state, dynamicPropertySelectorFeatureKey),
    };
  }),
  on(setSelectedPortfolioId, (state, action) => ({
    ...state,
    portfolioId: action.portfolioId,
  })),
  on(setPropertySingleMode, (state, action) => ({
    ...state,
    singleModeEnabled: action.singleMode,
  })),
  on(setSelectedPropertyIds, (state, action) => {
    const newState = {
      ...state,
      propertyIds: action.propertyIds,
      allPropertiesSelected: action.allPropertiesSelected,
    };
    return newState;
  }),
  on(setPortfolioUPPDState, (state, action) => ({
    ...state,
    portfolioUppdState: action.data,
  })),
  on(clearPortfolioUPPDState, (state, action) => ({
    ...state,
    portfolioUppdState: null,
  })),
  on(setCollectionUPPDState, (state, action) => ({
    ...state,
    collectionUppdState: action.data,
  })),
  on(clearPortfolioUPPDState, (state, action) => ({
    ...state,
    collectionUppdState: null,
  })),
  on(setSelectedCollectionIds, (state, action) => {
    const newState = {
      ...state,
      collectionIds: action.collectionIds,
      allCollectionsSelected: action.allCollectionsSelected,
    };
    return newState;
  }),
  on(removeSelectedCollectionId, (state, action) => {
    const newState = {
      ...state,
      collectionIds: state.collectionIds?.filter(id => id !== action.collectionId),
    };
    return newState;
  }),
  on(setPropertySelectorSwitchStatus, (state, action) => ({
    ...state,
    propertySelectorSwitchStatus: action.propertySelectorSwitchStatus,
  })),
  on(setJoinedProperties, (state, action) => ({
    ...state,
    joinProperties: action.state,
  })),
  on(setDoNotStore, (state, action) => ({
    ...state,
    doNotStore: action.doNotStore,
  })),
  on(restorePropertySelectorState, (state, action) => ({
    ...state,
    propertySelectorSwitchStatus: action.propertySelectorSwitchStatus,
    portfolioId: action.portfolioId,
    collectionIds: action.collectionIds,
  }))
);
